import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClipboardList, Lock, Unlock, Eye, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import { formatSectorLabel } from "@/lib/sector-labels";

interface AdminAssessment {
  id: string;
  organizationName: string;
  sector: string;
  country: string | null;
  reportingYear: number;
  status: string;
  completenessPercent: number;
  locked: boolean;
  createdAt: string;
  updatedAt: string;
  workspaceName: string | null;
}

interface AssessmentsResponse {
  data: AdminAssessment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

async function fetchAssessments(page: number, search: string, statusFilter: string): Promise<AssessmentsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "10" });
  if (search) params.set("search", search);
  if (statusFilter !== "all") params.set("status", statusFilter);
  const response = await fetch(`/api/admin/assessments?${params}`, { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch assessments");
  return response.json();
}

async function toggleLock(id: string) {
  const response = await fetch(`/api/admin/assessments/${id}/toggle-lock`, {
    method: "PATCH",
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to toggle lock");
  }
  return response.json();
}

export default function AdminAssessments() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [assessmentToDelete, setAssessmentToDelete] = useState<AdminAssessment | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/assessments", page, debouncedSearch, statusFilter],
    queryFn: () => fetchAssessments(page, debouncedSearch, statusFilter),
    enabled: user?.role === "admin",
  });

  const toggleLockMutation = useMutation({
    mutationFn: toggleLock,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assessments"] });
      toast({
        title: data.locked ? "Assessment locked" : "Assessment unlocked",
        description: data.locked
          ? "Assessment has been locked."
          : "Assessment has been unlocked.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/assessments/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to delete assessment");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assessments"] });
      setAssessmentToDelete(null);
      toast({
        title: "Assessment Deleted",
        description: "The assessment and all its data have been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
    },
  });

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  const statusStyle = (status: string) => {
    switch (status) {
      case "finalized": return { bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-500" };
      case "complete": return { bg: "bg-blue-50", text: "text-blue-600", dot: "bg-blue-500" };
      default: return { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" };
    }
  };

  const columns: ColumnDef<AdminAssessment>[] = [
    {
      id: "organization",
      header: "Organization",
      cell: (a) => <span className="font-semibold text-slate-900">{a.organizationName}</span>,
    },
    {
      id: "workspace",
      header: "Workspace",
      cell: (a) => <span className="text-slate-700">{a.workspaceName || "-"}</span>,
    },
    {
      id: "sector",
      header: "Sector",
      cell: (a) => (
        <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
          {formatSectorLabel(a.sector)}
        </span>
      ),
    },
    {
      id: "year",
      header: "Year",
      cell: (a) => <span className="text-slate-700">{a.reportingYear}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (a) => {
        const ss = statusStyle(a.status);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${ss.bg} ${ss.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
            {a.status}
          </span>
        );
      },
    },
    {
      id: "completeness",
      header: "Completeness",
      cell: (a) => (
        <div className="flex items-center gap-2">
          <div className="w-16 bg-slate-100 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full" style={{ width: `${a.completenessPercent}%` }} />
          </div>
          <span className="text-sm font-medium text-slate-700">{a.completenessPercent}%</span>
        </div>
      ),
    },
    {
      id: "locked",
      header: "Locked",
      cell: (a) =>
        a.locked ? (
          <Lock className="h-4 w-4 text-amber-500" />
        ) : (
          <Unlock className="h-4 w-4 text-slate-400" />
        ),
    },
    {
      id: "updated",
      header: "Updated",
      cell: (a) => <span className="text-sm text-slate-500">{a.updatedAt ? formatDate(a.updatedAt) : "-"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: (a) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl"
            onClick={() => setLocation(`/workflow/${a.id}`)}
            title={"View assessment"}
          >
            <Eye className="h-4 w-4 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl"
            onClick={() => toggleLockMutation.mutate(a.id)}
            disabled={toggleLockMutation.isPending}
            title={a.locked ? "Unlock assessment" : "Lock assessment"}
          >
            {a.locked ? (
              <Unlock className="h-4 w-4 text-emerald-600" />
            ) : (
              <Lock className="h-4 w-4 text-amber-500" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => setAssessmentToDelete(a)}
            title={"Delete assessment"}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
          {"Assessments Overview"}
        </h1>
        <p className="text-slate-500 mt-2">
          {"View and manage all assessments across companies"}
        </p>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {`Assessments (${data?.total ?? 0})`}
            </h3>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(a) => a.id}
            isLoading={isLoading}
            emptyState={{ icon: ClipboardList, title: "No assessments found" }}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: "Search by organization or workspace...",
            }}
            filters={
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px] rounded-2xl bg-slate-50">
                  <SelectValue placeholder={"Filter by status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{"All Status"}</SelectItem>
                  <SelectItem value="draft">{"Draft"}</SelectItem>
                  <SelectItem value="complete">{"Complete"}</SelectItem>
                  <SelectItem value="finalized">{"Finalized"}</SelectItem>
                </SelectContent>
              </Select>
            }
            pagination={
              data
                ? {
                    page,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </div>
      </div>

      <AlertDialog open={!!assessmentToDelete} onOpenChange={(open) => !open && setAssessmentToDelete(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">{"Delete Assessment"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              {`Are you sure you want to delete the assessment for <strong>${assessmentToDelete?.organizationName}</strong> (${assessmentToDelete?.reportingYear})? All data for this assessment will be permanently deleted, including all responses, reports, and uploaded documents. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">{"Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 rounded-2xl"
              onClick={() => assessmentToDelete && deleteMutation.mutate(assessmentToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? "Deleting..."
                : "Delete Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
